import { z } from 'zod';
import { booleanish, csvEnum, cursorPageSchema, elementStatusSchema, optionalBooleanish, severitySchema, } from './common.js';
export const deviceCreateSchema = z.object({
    name: z.string().min(1).max(120),
    siteId: z.string().uuid().optional(),
    deviceTypeId: z.string().uuid(),
    mgmtIp: z.string().ip().optional(),
    hostname: z.string().max(255).optional(),
    vendor: z.string().max(80).optional(),
    model: z.string().max(120).optional(),
    serial: z.string().max(120).optional(),
    pollIntervalS: z.number().int().min(10).max(3600).default(60),
    monitoringEnabled: z.boolean().default(true),
    isCritical: z.boolean().default(false),
    tags: z.array(z.string().max(40)).max(20).default([]),
    notes: z.string().max(4000).optional(),
});
export const deviceUpdateSchema = deviceCreateSchema.partial();
export const deviceFilterSchema = cursorPageSchema.extend({
    siteId: z.string().uuid().optional(),
    status: csvEnum(['UP', 'DOWN', 'DEGRADED', 'UNREACHABLE', 'MAINTENANCE', 'UNKNOWN']).optional(),
    category: z.string().optional(),
    search: z.string().max(120).optional(),
    monitoringEnabled: optionalBooleanish(),
});
/**
 * Credential write. There is no matching read schema, and that is deliberate:
 * no API route returns credential material, so no response type for it exists
 * anywhere in the codebase to be accidentally serialised.
 */
export const credentialWriteSchema = z.discriminatedUnion('protocol', [
    z.object({
        protocol: z.literal('SNMP_V2C'),
        community: z.string().min(1).max(200),
    }),
    z.object({
        protocol: z.literal('SNMP_V3'),
        username: z.string().min(1).max(100),
        authProtocol: z.enum(['MD5', 'SHA', 'SHA256', 'SHA512']),
        authKey: z.string().min(8).max(200),
        privProtocol: z.enum(['DES', 'AES', 'AES192', 'AES256']).optional(),
        privKey: z.string().min(8).max(200).optional(),
    }),
    z.object({
        protocol: z.literal('SSH'),
        username: z.string().min(1).max(100),
        password: z.string().max(200).optional(),
        privateKey: z.string().max(8000).optional(),
        port: z.number().int().min(1).max(65535).default(22),
    }),
    z.object({
        protocol: z.literal('HTTP_API'),
        baseUrl: z.string().url(),
        token: z.string().max(500).optional(),
        username: z.string().max(100).optional(),
        password: z.string().max(200).optional(),
    }),
]);
/**
 * Metric query. `step` is required and server-clamped — the response reports
 * which resolution actually answered (API spec §3).
 */
export const metricQuerySchema = z.object({
    keys: z
        .string()
        .transform((s) => s.split(',').map((k) => k.trim()))
        .pipe(z.array(z.string().min(1)).min(1).max(10)),
    instance: z.string().optional(),
    from: z.coerce.date(),
    to: z.coerce.date(),
    step: z.coerce.number().int().min(1).max(86_400),
    aggregate: z.enum(['avg', 'min', 'max', 'last']).default('avg'),
});
export const alarmFilterSchema = cursorPageSchema.extend({
    state: csvEnum(['RAISED', 'ACKNOWLEDGED', 'CLEARED', 'SUPPRESSED']).optional(),
    severity: csvEnum(['P1', 'P2', 'P3', 'P4']).optional(),
    siteId: z.string().uuid().optional(),
    elementId: z.string().uuid().optional(),
    /**
     * Default false. Showing 437 symptom alarms alongside their root is exactly
     * the alarm storm this system exists to prevent — they are one expand away.
     */
    includeSymptoms: booleanish(false),
    search: z.string().max(120).optional(),
});
export const alarmAckSchema = z.object({
    note: z.string().max(1000).optional(),
});
export const alarmDefinitionSchema = z
    .object({
    key: z
        .string()
        .regex(/^[a-z0-9]+(\.[a-z0-9-]+)+$/, 'Use dotted lowercase, e.g. device.cpu.high'),
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional(),
    severity: severitySchema,
    /**
     * Hold-down. Zero is allowed but discouraged for THRESHOLD conditions: a
     * single dropped SNMP response should not page anyone, and the default of
     * 0 exists only for STATE conditions where the transition is the event.
     */
    forDurationS: z.number().int().min(0).max(86_400).default(0),
    autoClear: z.boolean().default(true),
    enabled: z.boolean().default(true),
    runbookUrl: z.string().url().optional(),
})
    .and(z.discriminatedUnion('conditionType', [
    z.object({
        conditionType: z.literal('THRESHOLD'),
        condition: z.object({
            metricKey: z.string().min(1),
            operator: z.enum(['>', '>=', '<', '<=']),
            value: z.number(),
            /** Clears at a different value than it raises, to stop chattering. */
            clearValue: z.number().optional(),
        }),
    }),
    z.object({
        conditionType: z.literal('STATE'),
        condition: z.object({
            field: z.string().min(1),
            equals: z.array(z.string()).min(1),
        }),
    }),
    z.object({
        conditionType: z.literal('ABSENCE'),
        condition: z.object({
            metricKey: z.string().min(1),
            /** Raise when no sample has arrived for this long. */
            missingForS: z.number().int().min(30),
        }),
    }),
    z.object({
        conditionType: z.literal('RATE'),
        condition: z.object({
            metricKey: z.string().min(1),
            changePercent: z.number(),
            windowS: z.number().int().min(60),
        }),
    }),
    z.object({
        conditionType: z.literal('EXPRESSION'),
        condition: z.object({
            expression: z.string().min(1).max(500),
        }),
    }),
]));
export const maintenanceWindowSchema = z
    .object({
    title: z.string().min(1).max(160),
    description: z.string().max(4000).optional(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    notifyCustomers: z.boolean().default(false),
    targets: z
        .array(z.object({
        scopeType: z.enum([
            'DEVICE',
            'PON_PORT',
            'SPLITTER',
            'SPLICE_CLOSURE',
            'FIBER_SEGMENT',
            'SERVICE_DELIVERY_POINT',
            'INTERFACE',
            'UPSTREAM',
            'SITE',
        ]),
        scopeId: z.string().uuid(),
    }))
        .min(1, 'A maintenance window with no targets suppresses nothing'),
})
    .refine((v) => v.endsAt > v.startsAt, { message: 'End must be after start', path: ['endsAt'] });
export const deviceSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    mgmtIp: z.string().nullable(),
    vendor: z.string().nullable(),
    model: z.string().nullable(),
    status: elementStatusSchema,
    siteId: z.string().uuid().nullable(),
    siteName: z.string().nullable(),
    lastSeenAt: z.coerce.date().nullable(),
    monitoringEnabled: z.boolean(),
    isCritical: z.boolean(),
    openAlarmCount: z.number().int(),
});
//# sourceMappingURL=monitoring.js.map